import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import {
  ClipboardList,
  ArrowLeft,
  ArrowUpRight,
  Loader2,
  Save,
  Plus,
  Trash2,
  Building2,
  Wrench,
  ShieldAlert,
  CalendarClock,
  Users,
  DoorOpen,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Work Orders list page
// ---------------------------------------------------------------------------
export const WorkOrdersList = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/work-orders")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout
      title={t("pages.work_orders.title")}
      subtitle={t("pages.work_orders.subtitle")}
    >
      {loading ? (
        <div className="jc-card p-8 flex items-center gap-3 text-[var(--jc-text-soft)]">
          <Loader2 className="animate-spin" size={16} /> {t("work_orders_form.loading_body")}
        </div>
      ) : items.length === 0 ? (
        <div className="jc-card p-10 text-center border border-dashed border-[var(--jc-border-strong)]">
          <ClipboardList size={32} className="mx-auto text-[var(--jc-text-soft)]" />
          <div className="font-display text-xl mt-3">{t("work_orders_form.empty_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-2 max-w-md mx-auto">
            {t("wo_extra.empty_body_full")}
          </p>
          <Link
            to="/app/projects"
            data-testid="work-orders-empty-projects-link"
            className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-md jc-btn-primary text-sm font-semibold"
          >
            {t("work_orders_form.goto_projects")} <ArrowUpRight size={14} />
          </Link>
        </div>
      ) : (
        <div className="jc-card p-0 overflow-hidden" data-testid="work-orders-list">
          <table className="w-full text-sm">
            <thead className="bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
              <tr>
                <th className="text-left py-3 px-4 font-semibold">{t("work_orders_form.columns.customer_project")}</th>
                <th className="text-left py-3 px-4 font-semibold hidden md:table-cell">{t("work_orders_form.columns.linked_estimate")}</th>
                <th className="text-left py-3 px-4 font-semibold hidden md:table-cell">{t("work_orders_form.columns.generated")}</th>
                <th className="text-left py-3 px-4 font-semibold">{t("work_orders_form.columns.status")}</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {items.map((w) => (
                <tr key={w.id} data-testid={`work-order-row-${w.id}`}>
                  <td className="py-3 px-4">
                    <div className="font-semibold">{w.job_info?.customer_name || t("wo_extra.untitled_customer")}</div>
                    <div className="text-xs text-[var(--jc-text-soft)] truncate max-w-[280px]">
                      {w.job_info?.project_name || t("wo_extra.project_default")} · {w.job_info?.job_address || t("wo_extra.no_address")}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-[var(--jc-text-soft)] hidden md:table-cell">
                    {w.job_info?.estimate_name || "—"}
                  </td>
                  <td className="py-3 px-4 text-[var(--jc-text-soft)] hidden md:table-cell">
                    {formatDate(w.generated_at)}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-emerald-100 text-emerald-800 border border-emerald-200">
                      {t(`work_orders_form.status.${w.status || "ready"}`)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Link
                      to={`/app/work-orders/${w.id}`}
                      data-testid={`work-order-view-${w.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                    >
                      {t("work_orders_form.view_packet")} <ArrowUpRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
};

// ---------------------------------------------------------------------------
// Section header helper
// ---------------------------------------------------------------------------
const SectionHeader = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon size={14} className="text-[var(--jc-text-soft)]" />
    <div className="overline">{label}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Work Order detail page
// ---------------------------------------------------------------------------
export const WorkOrderDetail = () => {
  const { t } = useLang();
  const { wid } = useParams();
  const navigate = useNavigate();
  // Phase 5A · Publish current-page workspace hint.
  useLeviWorkspaceHint("work_order_detail", {
    open_document_type: "work_order",
    open_document_id: wid,
    current_section: "work_order_detail",
  });
  const [wo, setWo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    access_notes: "",
    safety_notes: "",
    schedule_notes: "",
    crew_assigned: "",
    tasks: [],
  });

  useEffect(() => {
    api
      .get(`/work-orders/${wid}`)
      .then((r) => {
        setWo(r.data);
        setForm({
          access_notes: r.data.access_notes || "",
          safety_notes: r.data.safety_notes || "",
          schedule_notes: r.data.schedule_notes || "",
          crew_assigned: r.data.crew_assigned || "",
          tasks: Array.isArray(r.data.tasks) ? r.data.tasks : [],
        });
      })
      .catch(() => {
        toast.error("Could not load crew packet");
        navigate("/app/work-orders");
      });
  }, [wid, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/work-orders/${wid}`, form);
      setWo(data);
      toast.success("Crew packet saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save crew packet");
    } finally {
      setSaving(false);
    }
  };

  const addTask = () => setForm((f) => ({ ...f, tasks: [...f.tasks, ""] }));
  const updateTask = (i, v) => setForm((f) => ({ ...f, tasks: f.tasks.map((t, idx) => (idx === i ? v : t)) }));
  const removeTask = (i) => setForm((f) => ({ ...f, tasks: f.tasks.filter((_, idx) => idx !== i) }));

  if (!wo) {
    return (
      <DashboardLayout title={t("pages.work_orders.detail_loading_title")} subtitle={t("pages.work_orders.detail_loading_subtitle")}>        <div />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={wo.job_info?.customer_name || "Crew Packet"}
      subtitle={`${wo.job_info?.project_name || "Project"} · ${wo.job_info?.job_address || "No address"}`}
      actions={
        <Link
          to="/app/work-orders"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
          data-testid="work-order-back-link"
        >
          <ArrowLeft size={14} /> {t("work_orders_form.back")}
        </Link>
      }
    >
      <div className="mb-4 jc-card p-4 border-l-4 border-l-emerald-500 flex items-start gap-3">
        <ClipboardList size={18} className="text-emerald-600 mt-0.5" />
        <div className="text-sm text-[var(--jc-text)]">
          <b>{t("work_orders_form.crew_safe_headline")}</b> {t("work_orders_form.crew_safe_body")}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Job info */}
          <section className="jc-card p-6" data-testid="work-order-job-info">
            <SectionHeader icon={Building2} label={t("work_orders_form.sections.job_info")} />
            <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <div><dt className="text-[var(--jc-text-soft)]">{t("wo_extra.customer")}</dt><dd className="font-semibold">{wo.job_info?.customer_name || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">{t("wo_extra.project")}</dt><dd>{wo.job_info?.project_name || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">{t("wo_extra.address")}</dt><dd>{wo.job_info?.job_address || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">{t("work_orders_form.columns.linked_estimate")}</dt><dd>{wo.job_info?.estimate_name || "—"}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">{t("work_orders_form.columns.generated")}</dt><dd>{formatDate(wo.generated_at)}</dd></div>
              <div><dt className="text-[var(--jc-text-soft)]">{t("work_orders_form.columns.status")}</dt><dd>{t(`work_orders_form.status.${wo.status || "ready"}`)}</dd></div>
            </dl>
          </section>

          {/* Scope */}
          <section className="jc-card p-6" data-testid="work-order-scope">
            <SectionHeader icon={ClipboardList} label={t("work_orders_form.sections.scope")} />
            {wo.scope_items?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="text-[var(--jc-text-soft)] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-2">{t("wo_extra.item")}</th>
                    <th className="text-right py-2 w-24">{t("wo_extra.qty")}</th>
                    <th className="text-left py-2 w-24 pl-3">{t("wo_extra.unit")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--jc-border)]">
                  {wo.scope_items.map((s) => (
                    <tr key={s.id}>
                      <td className="py-2 pr-3">
                        <div className="font-semibold">{s.name || "—"}</div>
                        {s.description && <div className="text-xs text-[var(--jc-text-soft)]">{s.description}</div>}
                      </td>
                      <td className="py-2 text-right font-mono-jc">{s.quantity ?? "—"}</td>
                      <td className="py-2 pl-3 text-[var(--jc-text-soft)]">{s.unit || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-sm text-[var(--jc-text-soft)]">{t("wo_extra.no_scope")}</div>
            )}
          </section>

          {/* Materials needed */}
          <section className="jc-card p-6" data-testid="work-order-materials">
            <SectionHeader icon={Wrench} label={t("work_orders_form.sections.materials")} />
            {wo.materials_needed?.length > 0 ? (
              <ul className="text-sm divide-y divide-[var(--jc-border)]">
                {wo.materials_needed.map((m, i) => (
                  <li key={i} className="py-2 flex items-center justify-between">
                    <span>{m.name}</span>
                    <span className="text-[var(--jc-text-soft)] font-mono-jc text-xs">{m.quantity} {m.unit}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-[var(--jc-text-soft)]">{t("wo_extra.no_materials")}</div>
            )}
          </section>

          {/* Tasks */}
          <section className="jc-card p-6" data-testid="work-order-tasks">
            <SectionHeader icon={ClipboardList} label={t("work_orders_form.sections.tasks")} />
            <div className="space-y-2">
              {form.tasks.map((task, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={task}
                    onChange={(e) => updateTask(i, e.target.value)}
                    data-testid={`work-order-task-${i}`}
                    className="flex-1 border border-[var(--jc-border)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]/40"
                    placeholder={t("wo_extra.task_placeholder")}
                  />
                  <button
                    onClick={() => removeTask(i)}
                    data-testid={`work-order-task-remove-${i}`}
                    className="p-2 rounded-md text-[var(--jc-text-soft)] hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={addTask}
                data-testid="work-order-task-add"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--jc-blue)] hover:underline"
              >
                <Plus size={12} /> {t("wo_extra.add_task")}
              </button>
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <section className="jc-card p-6" data-testid="work-order-access">
            <SectionHeader icon={DoorOpen} label={t("work_orders_form.sections.access_notes")} />
            <textarea
              value={form.access_notes}
              onChange={(e) => setForm({ ...form, access_notes: e.target.value })}
              data-testid="work-order-access-input"
              className="w-full border border-[var(--jc-border)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]/40 min-h-[80px]"
              placeholder={t("wo_extra.access_placeholder")}
            />
          </section>

          <section className="jc-card p-6" data-testid="work-order-safety">
            <SectionHeader icon={ShieldAlert} label={t("work_orders_form.sections.safety_notes")} />
            <textarea
              value={form.safety_notes}
              onChange={(e) => setForm({ ...form, safety_notes: e.target.value })}
              data-testid="work-order-safety-input"
              className="w-full border border-[var(--jc-border)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]/40 min-h-[80px]"
              placeholder={t("wo_extra.safety_placeholder")}
            />
          </section>

          <section className="jc-card p-6" data-testid="work-order-schedule">
            <SectionHeader icon={CalendarClock} label={t("work_orders_form.sections.schedule_notes")} />
            <textarea
              value={form.schedule_notes}
              onChange={(e) => setForm({ ...form, schedule_notes: e.target.value })}
              data-testid="work-order-schedule-input"
              className="w-full border border-[var(--jc-border)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]/40 min-h-[80px]"
              placeholder={t("wo_extra.schedule_placeholder")}
            />
          </section>

          <section className="jc-card p-6" data-testid="work-order-crew">
            <SectionHeader icon={Users} label={t("work_orders_form.sections.crew_assigned")} />
            <input
              type="text"
              value={form.crew_assigned}
              onChange={(e) => setForm({ ...form, crew_assigned: e.target.value })}
              data-testid="work-order-crew-input"
              className="w-full border border-[var(--jc-border)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]/40"
              placeholder={t("wo_extra.crew_placeholder")}
            />
          </section>

          <button
            onClick={save}
            disabled={saving}
            data-testid="work-order-save-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md jc-btn-blue text-sm font-semibold disabled:opacity-60"
          >
            {saving ? <><Loader2 className="animate-spin" size={14} /> {t("work_orders_form.saving")}</> : <><Save size={14} /> {t("work_orders_form.save_changes")}</>}
          </button>
        </aside>
      </div>
    </DashboardLayout>
  );
};

export default WorkOrdersList;
